import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import deezerAPI from './deezerApi';

function LyricUploadForm({ onSuccess }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [lyricText, setLyricText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingLyrics, setIsLoadingLyrics] = useState(false);
  
  const textareaRef = useRef(null);
  const MAX_CHARS = 500;
  const MAX_LINES = 9;

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    getUser();
  }, []);

  const searchSongs = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      alert('Escribe el nombre de una canción o artista');
      return;
    }

    setIsSearching(true);
    setSearchResults([]);

    try {
      const result = await deezerAPI.searchTracks(searchQuery, 15);

      if (result && result.data && result.data.length > 0) {
        const songs = result.data.map((track) => ({
          id: track.id,
          title: track.title,
          artist: track.artist.name,
          album: track.album.title || '',
          coverImage: track.album.cover_big || track.album.cover_medium || track.album.cover,
          artistForLyrics: track.artist.name,
          titleForLyrics: track.title,
        }));

        setSearchResults(songs);
        
        if (songs.length === 0) {
          alert('No se encontraron resultados. Intenta con otro término.');
        }
      } else {
        alert('No se encontraron resultados. Verifica tu API key de RapidAPI.');
      }

    } catch (error) {
      console.error('Error buscando canciones:', error);
      alert('Error al buscar canciones. Verifica tu configuración de API.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectTrack = async (track) => {
    setSelectedTrack(track);
    setSearchResults([]);
    setSearchQuery('');
    setLyricText('');

    if (track.artistForLyrics && track.titleForLyrics) {
      await fetchLyricsFromAPI(track.artistForLyrics, track.titleForLyrics);
    }
  };

  const fetchLyricsFromAPI = async (artist, title) => {
    setIsLoadingLyrics(true);
    try {
      const response = await fetch(
        `https://api.lyrics.ovh/v1/${encodeURIComponent(artist)}/${encodeURIComponent(title)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.lyrics) {
          setLyricText(data.lyrics);
          alert(`¡Letra cargada! Ahora SELECCIONA el fragmento que quieres (máximo ${MAX_LINES} líneas) y haz clic en "✂️ Usar Selección"`);
        } else {
          alert('No se encontraron las lyrics. Puedes escribirlas manualmente.');
        }
      } else {
        alert('No se encontraron las lyrics para esta canción. Puedes escribirlas manualmente.');
      }
    } catch (error) {
      console.log('Error obteniendo lyrics:', error);
      alert('No se pudieron cargar las lyrics automáticamente. Escríbelas manualmente.');
    } finally {
      setIsLoadingLyrics(false);
    }
  };

  const cleanText = (text) => {
    // Eliminar líneas vacías y espacios extra
    const lines = text.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    return lines.join('\n');
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    const lines = newText.split('\n');
    const nonEmptyLines = lines.filter(line => line.trim().length > 0);

    // Si excede el límite de líneas, no permitir más
    if (nonEmptyLines.length > MAX_LINES) {
      alert(`⚠️ Máximo ${MAX_LINES} líneas permitidas`);
      return;
    }

    setLyricText(newText);
  };

  const handleTextBlur = () => {
    // Limpiar espacios vacíos solo cuando el usuario termina de editar
    if (lyricText.trim()) {
      const cleaned = cleanText(lyricText);
      setLyricText(cleaned);
    }
  };

  const handleUseSelection = () => {
    if (!textareaRef.current) return;

    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const selectedText = lyricText.substring(start, end);

    if (!selectedText || selectedText.trim().length === 0) {
      alert('⚠️ Por favor, selecciona el texto que quieres usar con el mouse');
      return;
    }

    const cleaned = cleanText(selectedText);
    const lineCount = cleaned.split('\n').filter(line => line.trim().length > 0).length;

    if (lineCount > MAX_LINES) {
      alert(`❌ La selección tiene ${lineCount} líneas. Máximo ${MAX_LINES}. Selecciona menos texto.`);
      return;
    }

    setLyricText(cleaned);
    alert(`✅ Fragmento listo (${lineCount} líneas). Ahora puedes publicarlo.`);
  };

  const handleDeselectTrack = () => {
    setSelectedTrack(null);
    setLyricText('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert('Debes iniciar sesión para subir lyrics');
      return;
    }

    if (!selectedTrack) {
      alert('Debes seleccionar una canción');
      return;
    }

    if (!lyricText.trim()) {
      alert('Escribe o selecciona un lyric');
      return;
    }

    // Limpiar el texto antes de validar y enviar
    const cleanedText = cleanText(lyricText);
    const lineCount = cleanedText.split('\n').filter(line => line.trim().length > 0).length;

    if (lineCount > MAX_LINES) {
      alert(`El lyric no puede tener más de ${MAX_LINES} líneas. Usa el botón "✂️ Usar Selección" para acortar el texto.`);
      return;
    }

    setIsSubmitting(true);

    try {
      let trackId;

      // Primero intentar obtener el track existente
      const { data: existingTrack } = await supabase
        .from('tracks')
        .select('id')
        .eq('title', selectedTrack.title)
        .eq('artist', selectedTrack.artist)
        .maybeSingle();

      if (existingTrack) {
        trackId = existingTrack.id;
      } else {
        // Intentar insertar el nuevo track (dejar que Supabase genere el UUID automáticamente)
        const { data: newTrack, error: trackError } = await supabase
          .from('tracks')
          .insert({
            title: selectedTrack.title,
            artist: selectedTrack.artist,
            album: selectedTrack.album || null,
            cover_image: selectedTrack.coverImage,
          })
          .select()
          .single();

        // Si hay un error de clave duplicada, volver a buscar el track
        if (trackError) {
          if (trackError.code === '23505') {
            // Error de clave duplicada - otro usuario insertó el track al mismo tiempo
            console.log('Track insertado por otro usuario, buscando...');
            const { data: retryTrack, error: retryError } = await supabase
              .from('tracks')
              .select('id')
              .eq('title', selectedTrack.title)
              .eq('artist', selectedTrack.artist)
              .maybeSingle();

            if (retryError || !retryTrack) {
              throw trackError; // Si aún falla, lanzar el error original
            }
            trackId = retryTrack.id;
          } else {
            throw trackError; // Otro tipo de error
          }
        } else {
          trackId = newTrack.id;
        }
      }

      const { data: newLyric, error: lyricError } = await supabase
        .from('lyrics')
        .insert({
          user_id: currentUser.id,
          track_id: trackId,
          content: cleanedText,
          likes_count: 0,
        })
        .select(`
          *,
          users (
            id,
            username,
            full_name
          ),
          tracks (
            id,
            title,
            artist,
            album,
            cover_image
          )
        `)
        .single();

      if (lyricError) throw lyricError;

      setSelectedTrack(null);
      setLyricText('');
      setSearchQuery('');

      alert('¡Lyric publicado exitosamente! 🎉');

      if (onSuccess) {
        onSuccess(newLyric);
      }

    } catch (error) {
      console.error('Error subiendo lyric:', error);

      // Mensajes de error más específicos
      let errorMessage = 'Error al publicar el lyric. ';

      if (error.code === '23505') {
        errorMessage += 'Ya existe un lyric idéntico. Intenta con un fragmento diferente.';
      } else if (error.code === '23503') {
        errorMessage += 'Problema con la canción seleccionada. Por favor selecciona otra canción.';
      } else if (error.message && error.message.includes('track')) {
        errorMessage += 'Error al guardar la información de la canción.';
      } else if (error.message && error.message.includes('lyric')) {
        errorMessage += 'Error al guardar el lyric.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Intenta de nuevo.';
      }

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={styles.notLoggedIn}>
          <p>Debes iniciar sesión para subir lyrics</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Subir Lyric</h2>

      {!selectedTrack && (
        <form onSubmit={searchSongs} style={styles.searchForm}>
          <input
            type="text"
            placeholder="Buscar canción o artista..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          <button 
            type="submit" 
            disabled={isSearching}
            style={{
              ...styles.searchButton,
              ...(isSearching ? styles.buttonDisabled : {})
            }}
          >
            {isSearching ? 'Buscando...' : 'Buscar'}
          </button>
        </form>
      )}

      {searchResults.length > 0 && (
        <div style={styles.resultsContainer}>
          <p style={styles.resultsTitle}>Resultados:</p>
          {searchResults.map((track) => (
            <div
              key={track.id}
              onClick={() => handleSelectTrack(track)}
              style={styles.resultItem}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f8f8';
              }}
            >
              <img 
                src={track.coverImage} 
                alt={track.title}
                style={styles.resultCover}
              />
              <div style={styles.resultInfo}>
                <h4 style={styles.resultTitle}>{track.title}</h4>
                <p style={styles.resultArtist}>{track.artist}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedTrack && (
        <div style={styles.selectedTrack}>
          <p style={styles.selectedLabel}>Canción seleccionada:</p>
          <div style={styles.selectedTrackCard}>
            <img 
              src={selectedTrack.coverImage} 
              alt={selectedTrack.title}
              style={styles.selectedCover}
            />
            <div style={styles.selectedInfo}>
              <h3 style={styles.selectedTitle}>{selectedTrack.title}</h3>
              <p style={styles.selectedArtist}>{selectedTrack.artist}</p>
            </div>
            <button
              onClick={handleDeselectTrack}
              style={styles.changeButton}
            >
              Cambiar
            </button>
          </div>
        </div>
      )}

      {selectedTrack && (
        <>
          {isLoadingLyrics && (
            <div style={styles.loadingLyrics}>
              <p>🎵 Cargando letra completa...</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} style={styles.lyricForm}>
            <div style={styles.instructionBox}>
              <p style={styles.instruction}>
                {(() => {
                  const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
                  if (lineCount > MAX_LINES) {
                    return `⚠️ Tienes ${lineCount} líneas (máximo ${MAX_LINES}). SELECCIONA el fragmento que quieres y haz clic en "✂️ Usar Selección"`;
                  } else if (lyricText) {
                    return `✨ Si quieres acortar el texto: SELECCIONA el fragmento deseado y haz clic en "✂️ Usar Selección". Máximo ${MAX_LINES} líneas`;
                  } else {
                    return `📝 Escribe manualmente (máximo ${MAX_LINES} líneas). Los espacios vacíos se eliminarán automáticamente.`;
                  }
                })()}
              </p>
            </div>

            <textarea
              ref={textareaRef}
              placeholder="La letra aparecerá aquí automáticamente..."
              value={lyricText}
              onChange={handleTextChange}
              onBlur={handleTextBlur}
              style={{
                ...styles.textarea,
                ...((() => {
                  const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
                  return lineCount > MAX_LINES;
                })() ? styles.textareaError : {})
              }}
              rows={12}
            />

            {lyricText && (() => {
              const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
              return lineCount > MAX_LINES;
            })() && (
              <button
                type="button"
                onClick={handleUseSelection}
                style={styles.useSelectionButton}
              >
                ✂️ Usar Selección (Selecciona el texto primero)
              </button>
            )}

            <div style={styles.formFooter}>
              <span style={{
                ...styles.charCounter,
                color: (() => {
                  const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
                  if (lineCount > MAX_LINES) return '#d32f2f';
                  if (lineCount > MAX_LINES * 0.8) return '#ff9800';
                  return '#888';
                })()
              }}>
                {(() => {
                  const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
                  return `${lineCount} / ${MAX_LINES} líneas`;
                })()}
                {(() => {
                  const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
                  return lineCount > MAX_LINES ? ` (${lineCount - MAX_LINES} líneas de más)` : '';
                })()}
              </span>
              <button
                type="submit"
                disabled={(() => {
                  const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
                  return isSubmitting || !lyricText.trim() || lineCount > MAX_LINES;
                })()}
                style={{
                  ...styles.submitButton,
                  ...((() => {
                    const lineCount = lyricText.split('\n').filter(line => line.trim().length > 0).length;
                    return isSubmitting || !lyricText.trim() || lineCount > MAX_LINES;
                  })() ? styles.buttonDisabled : {})
                }}
              >
                {isSubmitting ? 'Publicando...' : 'Publicar Lyric'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  title: {
    marginTop: 0,
    marginBottom: '25px',
    fontSize: '24px',
    fontWeight: '700',
    color: '#222',
  },
  notLoggedIn: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
  },
  searchForm: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    padding: '12px 16px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.3s',
    color: '#333',
  },
  searchButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  resultsContainer: {
    marginBottom: '20px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  resultsTitle: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  resultItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f8f8f8',
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  resultCover: {
    width: '60px',
    height: '60px',
    borderRadius: '6px',
    objectFit: 'cover',
    flexShrink: 0,
  },
  resultInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  resultTitle: {
    margin: '0 0 4px 0',
    fontSize: '15px',
    fontWeight: '600',
    color: '#222',
  },
  resultArtist: {
    margin: 0,
    fontSize: '14px',
    color: '#666',
  },
  selectedTrack: {
    marginBottom: '20px',
  },
  selectedLabel: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  selectedTrackCard: {
    display: 'flex',
    gap: '15px',
    padding: '15px',
    backgroundColor: '#eff6ff',
    borderRadius: '10px',
    border: '2px solid #2563eb',
  },
  selectedCover: {
    width: '70px',
    height: '70px',
    borderRadius: '6px',
    objectFit: 'cover',
    flexShrink: 0,
  },
  selectedInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  selectedTitle: {
    margin: '0 0 6px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#222',
  },
  selectedArtist: {
    margin: 0,
    fontSize: '14px',
    color: '#666',
  },
  changeButton: {
    alignSelf: 'center',
    padding: '8px 16px',
    backgroundColor: 'white',
    color: '#2563eb',
    border: '2px solid #2563eb',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  loadingLyrics: {
    textAlign: 'center',
    padding: '20px',
    backgroundColor: '#eff6ff',
    borderRadius: '8px',
    marginBottom: '15px',
    color: '#2563eb',
    fontWeight: '600',
  },
  lyricForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  instructionBox: {
    backgroundColor: '#fff3cd',
    padding: '12px',
    borderRadius: '8px',
    borderLeft: '4px solid #ffc107',
  },
  instruction: {
    margin: 0,
    fontSize: '14px',
    color: '#856404',
    fontWeight: '500',
  },
  textarea: {
    width: '100%',
    padding: '15px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    resize: 'vertical',
    outline: 'none',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    transition: 'border-color 0.3s',
    color: '#333',
    backgroundColor: '#fff',
  },
  textareaError: {
    border: '2px solid #d32f2f',
    backgroundColor: '#ffebee',
  },
  useSelectionButton: {
    padding: '12px 20px',
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  formFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
  },
  charCounter: {
    fontSize: '13px',
    fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
};

export default LyricUploadForm;